import { APP_INITIALIZER, NgModule } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'

//-- Core --//
import { CoreModule } from '@core/core.module'
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core'

//-- App --//
import { AppService, AppServiceModule } from '@app/app.service'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'

//-- Services --//
import { HttpUserService } from '@app/data/service/http-user.service'
import { DataService } from '@data/service/data.service'
import { InnplaceService } from '@data/service/innplace.service'
import { PeopleService } from '@data/service/people.service'

//-- Store --//
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http'
import { StorageModule } from '@data/store/storage.module'
import { SharedComponentsModule } from './shared/components/shared-components.module'

import { AppNotificationService } from '@core/app-notification.service'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular'
import { ConfigService } from './shared/services/config.service'

interface KeycloakConfig {
   keycloak: {
      authUrl: string
      realm: string
      clientId: string
   }
}

function initializeKeycloak(keycloak: KeycloakService, httpClient: HttpClient) {
   return () => {
      return httpClient
         .get<KeycloakConfig>(`assets/configs/properties.json?ts=${Date.now()}`)
         .toPromise()
         .then((kc) => {
            return keycloak.init({
               config: {
                  url: kc.keycloak.authUrl,
                  realm: kc.keycloak.realm,
                  clientId: kc.keycloak.clientId,
               },
               initOptions: {
                  enableLogging: true,
                  checkLoginIframe: false,
                  flow: 'standard',
                  onLoad: 'check-sso',
                  responseMode: 'query',
               },
               enableBearerInterceptor: true,
               bearerPrefix: 'Bearer ',
            })
         })
   }
}

@NgModule({
   declarations: [AppComponent],
   imports: [
      AppServiceModule,
      BrowserModule,
      BrowserAnimationsModule,
      HttpClientModule,
      CoreModule.forRoot(),
      TranslateModule.forRoot({
         defaultLanguage: 'en-US',
         useDefaultLang: true,
         loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpBackend],
         },
      }),
      AppRoutingModule,
      StorageModule,
   ],
   exports: [BrowserModule, CoreModule, TranslateModule, SharedComponentsModule],
   providers: [
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [TranslateService],
         useFactory: initializeTranslation,
      },
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [ConfigService],
         useFactory: (configService: ConfigService) => {
            return () => configService.initRuntimeConfiguration()
         },
      },

      {
         provide: APP_INITIALIZER,
         useFactory: initializeKeycloak,
         multi: true,
         deps: [KeycloakService, HttpClient],
      },
      {
         provide: HTTP_INTERCEPTORS,
         useClass: KeycloakBearerInterceptor,
         multi: true,
      },
      KeycloakService,
      AppService,
      DataService,
      HttpUserService,
      PeopleService,
      InnplaceService,
      AppNotificationService,
   ],
   bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(handler: HttpBackend) {
   return new TranslateHttpLoader(new HttpClient(handler), './assets/i18n/')
}

function initializeTranslation(translate: TranslateService) {
   return () => {
      translate.setDefaultLang('en-US')
      const browserLang = navigator.language
      // Usa en-US se la lingua del browser non è supportata
      const langToUse = browserLang === 'en-US' ? browserLang : 'en-US'
      return translate.use(langToUse).toPromise()
   }
}

